// Presentation-layer analysis of a fillable report page, used by the inspector's
// Inspection Notes tab (FillThresholdReport).
//
// Everything here is DERIVED from the stored formMarkup geometry — nothing is
// keyed off a particular form. Every inspection type loads a different report
// (InspectionType.tsx swaps selectedForm + formMarkup), and the forms share no
// structure: one is 39 dense fields across four visual blocks, another is five
// large checkbox groups. So the layout has to be inferred, and has to degrade to
// "one flat list" whenever the inference finds nothing.
//
// This module is pure and has no React/Redux dependency, so it is unit-testable
// against real form fixtures.

import { buildElements, BuilderElement } from './PreviewTool/builder/elements';
import { SIGNATURE, formFontPt, pageKeysOf } from './PreviewTool/thresholdConstants';
import { PT_TO_MM, LINE_HEIGHT, PAGE_W_MM, PAGE_H_MM } from './PreviewTool/builder/coords';

export type FieldKind = BuilderElement['kind'];

export interface FieldBox {
  key: string;
  kind: FieldKind;
  isStatic: boolean;
  // The builder's explicit `width` for a text field, when one was set. Drives
  // overlayInfo's shrink-to-fit box, and wins over the derived width below.
  explicitWidthMm?: number;
  // Bounding box in A4 millimetres, the same coordinate space overlayInfo draws
  // in, so these map straight onto the scanned page image.
  topMm: number;
  leftMm: number;
  bottomMm: number;
  rightMm: number;
}

export interface Section {
  id: string;
  keys: string[];
  topMm: number;
  bottomMm: number;
}

// Fields whose anchors sit within this many mm of each other are on the same
// visual line of the printed form, and so read left-to-right.
export const ROW_BAND_MM = 3;

// A vertical gap has to clear these bounds to start a new section, whatever the
// adaptive threshold says. Keeps dense forms from shattering into dozens of
// sections and sparse ones from collapsing into a single block.
export const MIN_SECTION_GAP_MM = 10;
export const MAX_SECTION_GAP_MM = 40;

// jsPDF anchors text on the baseline; the glyph box sits above it.
const ascentMm = (fontPt: number) => fontPt * PT_TO_MM * 0.8;
const lineHeightMm = (fontPt: number) => fontPt * PT_TO_MM * LINE_HEIGHT;

// Rough printed width of a string; only used to size the highlight rectangle.
const textWidthMm = (text: string, fontPt: number) =>
  Math.max(20, `${text || ''}`.length * fontPt * PT_TO_MM * 0.5);

// Bounding box for a single normalised element, matching how overlayInfo stamps
// it: signatures are top-left anchored images, checkbox controls are squares
// sitting above the baseline, everything else is baseline-anchored text.
const boxOf = (el: BuilderElement) => {
  if (el.kind === 'signature') {
    return {
      topMm: el.topMm,
      leftMm: el.leftMm,
      bottomMm: el.topMm + (el.hMm || 10),
      rightMm: el.leftMm + (el.wMm || 40),
    };
  }

  if (el.kind === 'checkbox') {
    const size = el.fontPt * PT_TO_MM;
    return {
      topMm: el.topMm - size,
      leftMm: el.leftMm,
      bottomMm: el.topMm,
      rightMm: el.leftMm + size,
    };
  }

  if (el.kind === 'matrix') {
    const spanX = Math.max(0, (el.cols || 1) - 1) * (el.gapHMm || 0);
    const spanY = Math.max(0, (el.rows || 1) - 1) * (el.gapVMm || 0);
    return {
      topMm: el.topMm - ascentMm(el.fontPt),
      leftMm: el.leftMm,
      bottomMm: el.topMm + spanY + lineHeightMm(el.fontPt),
      rightMm: el.leftMm + spanX + textWidthMm('data', el.fontPt),
    };
  }

  const width = el.wMm && el.wMm > 0 ? el.wMm : textWidthMm(el.text || el.label, el.fontPt);
  return {
    topMm: el.topMm - ascentMm(el.fontPt),
    leftMm: el.leftMm,
    bottomMm: el.topMm - ascentMm(el.fontPt) + lineHeightMm(el.fontPt),
    rightMm: el.leftMm + width,
  };
};

// One box per FIELD. buildElements emits one element per checkbox option, so a
// group's boxes are merged into the union of its options.
export const buildFieldBoxes = (markup: Record<string, any>, tab: string): FieldBox[] => {
  const page = markup && markup[tab];
  if (!page) return [];

  const byKey = new Map<string, FieldBox>();

  buildElements(page, formFontPt(markup)).forEach(el => {
    const b = boxOf(el);
    const existing = byKey.get(el.key);

    if (existing) {
      existing.topMm = Math.min(existing.topMm, b.topMm);
      existing.leftMm = Math.min(existing.leftMm, b.leftMm);
      existing.bottomMm = Math.max(existing.bottomMm, b.bottomMm);
      existing.rightMm = Math.max(existing.rightMm, b.rightMm);
      return;
    }

    const explicitWidth = Number(page[el.key] && page[el.key].width);

    byKey.set(el.key, {
      key: el.key,
      kind: el.kind,
      isStatic: !!(page[el.key] && page[el.key].isStatic),
      explicitWidthMm:
        Number.isFinite(explicitWidth) && explicitWidth > 0 ? explicitWidth : undefined,
      ...b,
    });
  });

  return Array.from(byKey.values());
};

// Reading order: group into visual rows, then left-to-right within each row.
// The old getSortedItems compared `top` only, so side-by-side fields fell back
// to object-insertion order and read out of sequence.
export const groupIntoRows = (boxes: FieldBox[], rowBandMm = ROW_BAND_MM): FieldBox[][] => {
  const byTop = [...boxes].sort((a, b) => a.topMm - b.topMm);

  const rows: FieldBox[][] = [];
  let anchorTop = Number.NEGATIVE_INFINITY;

  byTop.forEach(box => {
    // Compare against the row's anchor, never the previous item, so a long run
    // of closely-spaced fields cannot chain into one giant row.
    if (!rows.length || box.topMm - anchorTop > rowBandMm) {
      rows.push([box]);
      anchorTop = box.topMm;
      return;
    }
    rows[rows.length - 1].push(box);
  });

  return rows.map(row => [...row].sort((a, b) => a.leftMm - b.leftMm));
};

export const sortReadingOrder = (boxes: FieldBox[], rowBandMm = ROW_BAND_MM): FieldBox[] =>
  groupIntoRows(boxes, rowBandMm).reduce((acc: FieldBox[], row) => acc.concat(row), []);

// overlayInfo shrinks text to fit `pageWidth - left - 3mm` when a field has no
// explicit width. Taken literally on screen that would make every empty input
// run to the page edge and overlap its neighbours — projectName at left 38 would
// swallow date at left 166 on the same row.
export const FIELD_RIGHT_MARGIN_MM = 3;
// Breathing room so an input stops short of the next field rather than touching.
export const FIELD_GUTTER_MM = 2;
// Even a tiny field needs to be clickable.
export const MIN_FIELD_WIDTH_MM = 8;

// Usable width for every field, keyed by field name. Serves double duty: the
// on-screen input width AND the character-capacity denominator.
export const computeFitWidths = (boxes: FieldBox[]): Record<string, number> => {
  const widths: Record<string, number> = {};

  groupIntoRows(boxes).forEach(row => {
    row.forEach((box, index) => {
      if (box.explicitWidthMm) {
        widths[box.key] = box.explicitWidthMm;
        return;
      }
      // Stop at whatever comes next on this row — a field, a checkbox group,
      // anything — otherwise fall through to the page margin.
      const next = row[index + 1];
      const limit = next ? next.leftMm - FIELD_GUTTER_MM : PAGE_W_MM - FIELD_RIGHT_MARGIN_MM;
      widths[box.key] = Math.max(MIN_FIELD_WIDTH_MM, limit - box.leftMm);
    });
  });

  return widths;
};

// A field name is only unique within its page, and the fill canvas now renders
// every page at once — so focus refs, the active field and the Enter-advance
// order are all keyed by `page::field`.
const REF_SEPARATOR = '::';

export const refKey = (page: string, key: string) => `${page}${REF_SEPARATOR}${key}`;

export const splitRefKey = (composite: string): { page: string; key: string } => {
  const at = `${composite}`.indexOf(REF_SEPARATOR);
  if (at < 0) return { page: '0', key: `${composite}` };
  return {
    page: composite.slice(0, at),
    key: composite.slice(at + REF_SEPARATOR.length),
  };
};

// How many characters fit in a box, given a measured average glyph width. The
// caller supplies the measurement (it needs a canvas), keeping this testable.
export const capacityChars = (boxWidthPx: number, avgCharPx: number): number => {
  if (!(avgCharPx > 0) || !(boxWidthPx > 0)) return 0;
  return Math.max(1, Math.floor(boxWidthPx / avgCharPx));
};

// Tick or clear one option of a checkbox/radio group.
//
// Options are identified by INDEX, never by `item.name`: the form builder writes
// a literal 'BOX-NAME' placeholder into every option it creates, so matching on
// the name made one click hit every option at once. Shared by the on-page canvas
// and the small-screen field list so the two cannot drift apart.
export const toggleGroupItem = (
  data: Record<string, any>,
  tab: string,
  key: string,
  index: number,
  checked: boolean,
): Record<string, any> => {
  const group = data[tab][key];
  const isRadio = group.inputType === 'radio';

  return {
    ...data,
    [tab]: {
      ...data[tab],
      [key]: {
        ...group,
        items: group.items.map((box: any, i: number) => {
          if (i === index) return { ...box, text: checked ? 'x' : ' ' };
          // Radio groups are single-choice: selecting one clears the rest.
          return isRadio ? { ...box, text: ' ' } : box;
        }),
      },
    },
  };
};

const median = (values: number[]): number => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Split the page into sections wherever the printed form leaves an unusually
// large vertical gap. Dense forms run 5-6mm between fields inside a block and
// 18-19mm between blocks, so an adaptive multiple of the median gap segments
// them without any per-form configuration.
//
// This is PURELY VISUAL: it never reorders fields and never touches data, so a
// mis-split only puts a divider in a slightly odd place.
export const groupIntoSections = (orderedBoxes: FieldBox[]): Section[] => {
  if (!orderedBoxes.length) return [];

  // Collapse to row anchors, preserving reading order.
  const rowTops: number[] = [];
  orderedBoxes.forEach(box => {
    const last = rowTops[rowTops.length - 1];
    if (last === undefined || box.topMm - last > ROW_BAND_MM) {
      rowTops.push(box.topMm);
    }
  });

  const gaps: number[] = [];
  rowTops.forEach((top, i) => {
    if (i > 0) gaps.push(top - rowTops[i - 1]);
  });

  const threshold = Math.min(
    MAX_SECTION_GAP_MM,
    Math.max(MIN_SECTION_GAP_MM, 2 * median(gaps)),
  );

  const sections: Section[] = [];
  let current: FieldBox[] = [];
  let anchorTop = Number.NEGATIVE_INFINITY;

  const flush = () => {
    if (!current.length) return;
    sections.push({
      id: `section-${sections.length}`,
      keys: current.map(b => b.key),
      topMm: Math.min(...current.map(b => b.topMm)),
      bottomMm: Math.max(...current.map(b => b.bottomMm)),
    });
    current = [];
  };

  orderedBoxes.forEach(box => {
    const isNewRow = box.topMm - anchorTop > ROW_BAND_MM;
    if (isNewRow) {
      if (current.length && box.topMm - anchorTop > threshold) {
        flush();
      }
      anchorTop = box.topMm;
    }
    current.push(box);
  });
  flush();

  return sections;
};

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

// Tokens that should read as acronyms rather than Title Case.
const ACRONYMS = new Set([
  'ASTM', 'ACI', 'PE', 'AR', 'AV', 'PSI', 'PSF', 'ID', 'NO', 'HVAC', 'QC', 'LF', 'FBC', 'PP',
]);

// Short words that stay lowercase unless they lead the label.
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'of', 'on', 'or', 'per', 'the', 'to', 'with',
]);

const castToken = (token: string, index: number): string => {
  // A token may itself be a '/'-separated compound, e.g. 'pe/ar'.
  if (token.indexOf('/') > -1) {
    return token
      .split('/')
      .map(part => castToken(part, index))
      .join('/');
  }

  const upper = token.toUpperCase();
  if (ACRONYMS.has(upper)) return upper;

  const lower = token.toLowerCase();
  if (index > 0 && STOP_WORDS.has(lower)) return lower;

  return token.charAt(0).toUpperCase() + token.slice(1);
};

// Turn a formMarkup key into something an inspector can read.
//
// Deliberately NOT a change to camelCaseToWords (docOvelayTool.ts) — that helper
// is shared with other components and with the builder.
export const humanizeKey = (raw: string): string => {
  if (!raw) return '';

  const spaced = `${raw}`
    // 'Comply,forThe' -> 'Comply, forThe'
    .replace(/,(\S)/g, ', $1')
    // 'airContent' -> 'air Content', 'astmC231' -> 'astm C231'
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    // 'ASTMCode' -> 'ASTM Code'
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  if (!spaced) return '';

  return spaced
    .split(' ')
    .map((token, index) => {
      // Keep trailing punctuation (commas) attached to the word.
      const match = token.match(/^([^,]*)(,?)$/);
      const word = match ? match[1] : token;
      const tail = match ? match[2] : '';
      return castToken(word, index) + tail;
    })
    .join(' ');
};

/**
 * What to call a field on screen.
 *
 * A field's key is now an opaque id (builder/fieldKeys.ts), so it cannot be
 * shown to anyone — the readable name lives on the field as `label`. Shown
 * VERBATIM: the Super Admin typed it, so it is not ours to re-case or re-space.
 *
 * Falls back to humanizing the key for every record created before labels
 * existed, where the key IS the name.
 */
export const fieldDisplayName = (key: string, item?: any): string => {
  const label = item && item.label;
  if (typeof label === 'string' && label.trim()) return label;
  return humanizeKey(key);
};

// The builder seeds new checkbox options with placeholder text; those should
// never reach an inspector.
export const isPlaceholderCaption = (caption?: string): boolean =>
  !caption || /^(LABEL|BOX-NAME)$/i.test(`${caption}`.trim());

export const optionLabel = (caption: string | undefined, index: number): string =>
  (isPlaceholderCaption(caption) ? `Option ${index + 1}` : `${caption}`);

// ---------------------------------------------------------------------------
// Input inference
// ---------------------------------------------------------------------------

export type InferredInput = 'text' | 'time' | 'date' | 'textarea';

const LONG_TEXT_KEY = /notes?|reason|description|comment|remarks?|explain|detail/i;
const LONG_TEXT_MIN_MAX = 200;

const NATIVE_TIME = /^\d{1,2}:\d{2}$/;
const NATIVE_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Pick a richer input than a bare text box where the key makes it unambiguous.
//
// The time/date branches are guarded on the CURRENT VALUE: a legacy value like
// '2:30 PM' renders blank in a native time input and would be wiped on blur, so
// a field only upgrades when it is empty or already in the native format.
export const inferInputType = (key: string, item: any, value: string): InferredInput => {
  // An explicit `multiline` from the form builder is authoritative — the admin
  // said this field wraps in the PDF, so it gets a multi-line control. The
  // key-name heuristic stays as a fallback so fields configured before the flag
  // existed keep behaving as they did.
  if (item && item.multiline) return 'textarea';

  const max = Number(item && item.max);
  if (LONG_TEXT_KEY.test(key) || (Number.isFinite(max) && max >= LONG_TEXT_MIN_MAX)) {
    return 'textarea';
  }

  const current = `${value ?? ''}`.trim();

  if (/time$/i.test(key) && (!current || NATIVE_TIME.test(current))) return 'time';
  if (/date$/i.test(key) && (!current || NATIVE_DATE.test(current))) return 'date';

  return 'text';
};

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export const isFieldFilled = (item: any): boolean => {
  if (!item) return false;

  if (Array.isArray(item.items)) {
    return item.items.some((box: any) => `${box.text ?? ''}`.trim() === 'x');
  }

  if (Array.isArray(item.matrix)) {
    return item.matrix.some((row: any[]) => row.some(cell => `${cell.text ?? ''}`.trim() !== ''));
  }

  return `${item.text ?? ''}`.trim() !== '';
};

// A field only counts toward progress if the inspector can actually act on it —
// auto-filled statics and the signature are excluded.
export const isCountable = (key: string, item: any): boolean =>
  key !== SIGNATURE && !!item && !item.isStatic;

export const countProgress = (
  markup: Record<string, any>,
  tab: string,
  keys: string[],
): { filled: number; total: number } => {
  const page = (markup && markup[tab]) || {};
  return keys.reduce(
    (acc, key) => {
      const item = page[key];
      if (!isCountable(key, item)) return acc;
      return { filled: acc.filled + (isFieldFilled(item) ? 1 : 0), total: acc.total + 1 };
    },
    { filled: 0, total: 0 },
  );
};

// ---------------------------------------------------------------------------
// Completion gate
// ---------------------------------------------------------------------------

// A report may not be marked Completed until its OUTCOME is recorded — a signed
// report that never says whether the work passed is the one thing it cannot be
// filed without.
//
// The outcome field is found by DISPLAY NAME, not by key: keys are opaque ids
// (builder/fieldKeys.ts) on anything created since the builder started
// generating them, so the label is the only readable thing to match on. Nothing
// here is keyed to a particular form — a report with no result field is simply
// not gated, which is how every form without one keeps working.
const RESULT_NAME = /result/i;

// Only a checkbox/radio GROUP is the outcome itself. Forms carry text fields
// beside it ('Inspection Result Other' on the St Pete report) that are a note
// ABOUT the outcome; typing in one is not choosing a result.
const isChoiceGroup = (item: any): boolean => !!item && Array.isArray(item.items);

export interface ResultGate {
  // False ONLY when the form has result group(s) and none of them is ticked.
  ok: boolean;
  // The first unanswered group — named in the message the inspector sees.
  label: string;
  // 1-based, matching the page tabs.
  pageNumber: number;
}

const GATE_OPEN: ResultGate = { ok: true, label: '', pageNumber: 0 };

export const checkResultSelected = (markup: Record<string, any>): ResultGate => {
  let firstEmpty: ResultGate | null = null;

  const pages = pageKeysOf(markup);
  for (let i = 0; i < pages.length; i += 1) {
    const page = markup[pages[i]] || {};
    const keys = Object.keys(page);
    for (let k = 0; k < keys.length; k += 1) {
      const item = page[keys[k]];
      // A status-driven row is not the inspector's to tick here — Activity
      // Status fills it (applyStatusToResult) — so it never holds the gate.
      if (
        isChoiceGroup(item) &&
        RESULT_NAME.test(fieldDisplayName(keys[k], item)) &&
        // Defined below with the rest of the status mapping; only ever called
        // at run time, after the module has loaded.
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        !isStatusDrivenResult(keys[k], item)
      ) {
        // Any ONE recorded outcome opens the gate. A form is free to repeat the
        // group per section, and demanding every one of them would block
        // reports the inspector has legitimately finished.
        if (isFieldFilled(item)) return GATE_OPEN;
        if (!firstEmpty) {
          firstEmpty = {
            ok: false,
            label: fieldDisplayName(keys[k], item),
            pageNumber: i + 1,
          };
        }
      }
    }
  }

  return firstEmpty || GATE_OPEN;
};

// ---------------------------------------------------------------------------
// Result driven by Activity Status
// ---------------------------------------------------------------------------

// The inspector records the outcome ONCE, as the inspection's Activity Status,
// and the form's result group follows it. Keyed by the status name, each entry
// lists the captions a form may use for that same outcome — compared after
// normalising, so 'N/A', 'NA' and 'n/a' are one caption.
const normCaption = (s?: string): string => `${s ?? ''}`.toLowerCase().replace(/[^a-z]/g, '');

const STATUS_RESULT_CAPTIONS: Record<string, string[]> = {
  pass: ['pass', 'passed'],
  partialpass: ['partialpass'],
  failed: ['fail', 'failed'],
  cancelled: ['cancelled', 'canceled'],
  notapplicable: ['na', 'notapplicable'],
};

const optionIndexForStatus = (item: any, statusName: string): number => {
  const captions = STATUS_RESULT_CAPTIONS[normCaption(statusName)];
  if (!captions) return -1;
  return item.items.findIndex((box: any) => captions.includes(normCaption(box.caption)));
};

// Which option each outcome status ticks in a status-driven group, keyed by the
// normalised status name. null = the group is not driven by the status.
type OutcomeTargets = Record<string, number>;

const outcomeTargets = (key: string, item: any): OutcomeTargets | null => {
  if (!isChoiceGroup(item)) return null;
  const name = fieldDisplayName(key, item);

  // Only a result group that can say EVERY outcome is driven by the status.
  // That is the threshold / specialty "Results" row (Pass, Partial Pass, Fail,
  // Canceled, NA). Jurisdiction forms word theirs differently (Approved,
  // Rejected, Field Check…) and miss outcomes, so they stay the inspector's to
  // tick — locking them would leave options nobody could ever choose.
  if (RESULT_NAME.test(name)) {
    const statuses = Object.keys(STATUS_RESULT_CAPTIONS);
    const targets: OutcomeTargets = {};
    statuses.forEach(status => {
      targets[status] = optionIndexForStatus(item, status);
    });
    return statuses.every(status => targets[status] > -1) ? targets : null;
  }

  // Threshold Concrete has no Results row. Its outcome is two single "Yes"
  // boxes — "The concrete placement complies with…" (pass) and "…does not
  // comply, for the following reason" (fail). Partial Pass counts as a pass;
  // Cancelled and Not Applicable tick neither. Single-option only, so no
  // Yes/No question that happens to mention compliance is caught.
  if (item.items.length === 1) {
    const norm = normCaption(name);
    if (norm.includes('doesnotcomply')) return { failed: 0 };
    if (norm.includes('complies')) return { pass: 0, partialpass: 0 };
  }
  return null;
};

export const isStatusDrivenResult = (key: string, item: any): boolean => !!outcomeTargets(key, item);

export const hasStatusDrivenResult = (markup: Record<string, any>): boolean =>
  pageKeysOf(markup || {}).some(p =>
    Object.keys(markup[p] || {}).some(k => isStatusDrivenResult(k, markup[p][k])));

// Ticks the option matching `statusName` in every status-driven result group
// and clears the rest. A status that is not an outcome (Scheduled…) clears the
// group. Returns the SAME object when nothing changes, so callers can compare
// by identity and never dispatch — or save — a no-op.
export const applyStatusToResult = (
  markup: Record<string, any>,
  statusName: string,
): Record<string, any> => {
  if (!markup) return markup;
  let next = markup;
  pageKeysOf(markup).forEach(p => {
    const page = markup[p] || {};
    Object.keys(page).forEach(key => {
      const item = page[key];
      const targets = outcomeTargets(key, item);
      if (!targets) return;
      const target = targets[normCaption(statusName)] ?? -1;
      const unchanged = item.items.every(
        (box: any, i: number) => (`${box.text ?? ''}`.trim() === 'x') === (i === target),
      );
      if (unchanged) return;
      const nextPage = next === markup ? { ...page } : { ...next[p] };
      nextPage[key] = {
        ...item,
        items: item.items.map((box: any, i: number) => ({ ...box, text: i === target ? 'x' : ' ' })),
      };
      next = { ...next, [p]: nextPage };
    });
  });
  return next;
};

// The page image is stretched to fill a whole A4 page by overlayInfo, so mm map
// onto it as a pure linear scale. Expressing highlights as percentages means the
// overlay stays aligned at any rendered size with no measurement.
export const mmToPercent = {
  x: (mm: number) => (mm / PAGE_W_MM) * 100,
  y: (mm: number) => (mm / PAGE_H_MM) * 100,
};

// ---------------------------------------------------------------------------
// Report readiness
// ---------------------------------------------------------------------------

// What still stands between the form and Create Report. Replaces the
// inspector's "Completed" tick: the form is ready the moment nothing is listed.
//
// Two sources:
//  - fields the form builder marked `required` (FieldEditorCard) and that are
//    still blank. Static fields fill themselves, the signature is stamped, and
//    a status-driven outcome follows Activity Status — Cancelled on Concrete
//    legitimately ticks nothing — so none of those can be missing.
//  - an unanswered result group the inspector ticks by hand (jurisdiction
//    forms), the rule checkResultSelected already enforced.
export interface MissingField {
  page: string;
  key: string;
  label: string;
  // 1-based, matching the page tabs.
  pageNumber: number;
}

export const isRequiredField = (key: string, item: any): boolean =>
  !!item && !!item.required && isCountable(key, item) && !isStatusDrivenResult(key, item);

// A city / jurisdiction form's hand-ticked result group (City of Orlando
// "Results of this inspection": Approved, Rejected, Field Check…), still
// unanswered. Create Report needs ONE result ticked (checkResultSelected), so
// the whole group is drawn as required until any option is ticked — and again
// if it is unticked.
export const isResultUnanswered = (key: string, item: any, markup: Record<string, any>): boolean =>
  isChoiceGroup(item) &&
  RESULT_NAME.test(fieldDisplayName(key, item)) &&
  !isStatusDrivenResult(key, item) &&
  !checkResultSelected(markup).ok;

export const findMissingForReport = (markup: Record<string, any>): MissingField[] => {
  if (!markup) return [];
  const missing: MissingField[] = [];
  pageKeysOf(markup).forEach((p, i) => {
    const page = markup[p] || {};
    Object.keys(page).forEach(key => {
      const item = page[key];
      if (isRequiredField(key, item) && !isFieldFilled(item)) {
        missing.push({ page: p, key, label: fieldDisplayName(key, item), pageNumber: i + 1 });
      }
    });
  });

  const gate = checkResultSelected(markup);
  if (!gate.ok) {
    const page = pageKeysOf(markup)[gate.pageNumber - 1];
    const key = Object.keys(markup[page] || {}).find(
      k => fieldDisplayName(k, markup[page][k]) === gate.label,
    );
    if (key && !missing.some(m => m.page === page && m.key === key)) {
      missing.push({ page, key, label: gate.label, pageNumber: gate.pageNumber });
    }
  }

  return missing.sort((a, b) => a.pageNumber - b.pageNumber);
};
